import React, { useState } from 'react';
import { Dropdown, ProgressBar, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';
import PickedShipmentLineRejectPanel from 'components/add_order/picklist/ProcessPicklist/PickedShipmentLineRejectPanel';

import { picklistApi, shipmentApi } from 'api';
import {
  getShipmentBadgeColor,
  BEFORE_PACKED_SHIPMENT_STATUSES,
} from 'constants/shipment';
import {
  convertPdfToHtml,
  showAwbPdf,
  generateBlueDartAwbPdf,
} from 'utils/pdfGenerators';

const PickedShipmentInfo = ({ picklistId, shipmentData, onRefresh }) => {
  const [showAll, setShowAll] = useState(false);
  const [showShipmentLineRejectPanel, setShowShipmentLineRejectPanel] =
    useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    action: null,
  });

  const {
    id: shipmentId,
    shipment_code,
    shipment_status,
    shipment_status_label,
    order,
    weight,
    package_width = 0,
    package_length = 0,
    package_height = 0,
    lines,
  } = shipmentData;

  const scannedLines = lines?.filter(
    line => line.shipment_line_status === 'picked',
  );

  const needToPickLines = lines?.filter(line =>
    BEFORE_PACKED_SHIPMENT_STATUSES.includes(line.shipment_line_status),
  );

  const handleConfirm = async () => {
    try {
      await modalConfig?.action();
      await onRefresh();
      toast.success(`${modalConfig?.title} completed successfully!`);
    } catch (error) {
      toast.error(`Failed to complete ${modalConfig?.title}`);
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleSplitShipment = () => {
    setModalConfig({
      title: 'Split Shipment',
      message:
        'The remaining items will be added to a new shipment. Are you sure you want to split the shipment?',
      action: () => picklistApi.splitPicklistShipment(picklistId, shipmentId),
    });
    setShowConfirmModal(true);
  };

  const handleSidelineShipment = () => {
    setModalConfig({
      title: 'Sideline Shipment',
      message:
        'The current shipment will be sidelined. Other shipments will need to be packed.',
      action: () => picklistApi.setPickedShipmentStatusToSidelined(picklistId),
    });
    setShowConfirmModal(true);
  };

  const handlePrintInvoiceAndAWBPdf = async shipmentId => {
    try {
      const response = await shipmentApi.getInvoiceDetail(shipmentId);

      const shipmentAWBFile = response?.shipment?.awb_file;
      const isBlueDart =
        response?.shipment?.shipping_provider?.shipping_provider_type ===
        'BLUEDART';

      if (isBlueDart) {
        generateBlueDartAwbPdf(response);
      } else if (shipmentAWBFile) {
        const awbHtml = await convertPdfToHtml(shipmentAWBFile);
        showAwbPdf(awbHtml, response);
      }
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  return (
    <>
      <div className="bg-light p-3">
        <div className="mb-3">
          <div className="mb-2">
            <div className="fs-10 fw-semibold mb-1">Shipment Code</div>
            <div className="fs-10 mb-1">{shipment_code}</div>
            <SubtleBadge bg={getShipmentBadgeColor(shipment_status)}>
              {shipment_status_label}
            </SubtleBadge>
          </div>

          <Dropdown align="start">
            <Dropdown.Toggle
              variant="light"
              size="sm"
              className="bg-200 border-0 fs-10 fw-semi-bold d-flex align-items-center py-1 px-2"
            >
              ACTIONS
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {shipment_status === 'ready_to_ship' ? (
                <Dropdown.Item
                  onClick={() => handlePrintInvoiceAndAWBPdf(shipmentId)}
                >
                  Print Invoice & AWB
                </Dropdown.Item>
              ) : null}
              {shipment_status === 'picked' ? (
                <>
                  <Dropdown.Item onClick={handleSplitShipment}>
                    Split Shipment
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setShowShipmentLineRejectPanel(true)}
                  >
                    Edit Shipment Items
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleSidelineShipment}>
                    Sideline Shipment
                  </Dropdown.Item>
                </>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="mb-3">
          <div>
            {scannedLines?.length}/ {needToPickLines?.length}
          </div>
          <ProgressBar
            now={(scannedLines?.length / needToPickLines?.length) * 100}
          />
        </div>

        <div className="d-flex flex-column">
          <div className="mb-2">
            <div className="fs-10 fw-semibold">Channel</div>
            <div className="fs-10">{order?.channel?.channel_type}</div>
          </div>

          {showAll && (
            <>
              <div className="mb-2">
                <div className="fs-10 fw-semibold">Sale Order Code</div>
                <div className="fs-10">{order?.id}</div>
              </div>

              <div className="mb-2">
                <div className="fs-10 fw-semibold">Package Dimensions (mm)</div>
                <div className="fs-10">{`${package_width} x ${package_length} x ${package_height}`}</div>
              </div>

              <div className="mb-2">
                <div className="fs-10 fw-semibold">Weight</div>
                <div className="fs-10">
                  {weight?.value} ({weight?.unit})
                </div>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-2">
          <span
            className="fs-10 cursor-pointer"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                Show Less <FontAwesomeIcon icon="chevron-up" />
              </>
            ) : (
              <>
                Show All <FontAwesomeIcon icon="chevron-down" />
              </>
            )}
          </span>
        </div>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalConfig.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalConfig.message}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <PickedShipmentLineRejectPanel
        show={showShipmentLineRejectPanel}
        onHide={() => setShowShipmentLineRejectPanel(false)}
        picklistId={picklistId}
        onRefresh={onRefresh}
      />
    </>
  );
};

export default PickedShipmentInfo;
